<template>
  <form action="" method="post" class="contact-form" autocomplete="off">
    <div v-if="loading" class="loading"></div>
    <div v-else-if="showBanner" class="alert alert-primary">
      <p class="text-center m-0" v-if="!isDistinctAddress">
        {{ $t("labels.dataLinkedTo") }}
        <strong>{{ aspspEmail || order.billingEmail }}</strong>
        {{ $t("labels.haveBeenUsedToFillTheForm") }}
        <br />
        {{ $t("labels.youWillBeDeliveredAt") }} :
        <strong>{{ aspspFullAddress || fullAddress }}</strong
        >.
      </p>
      <p class="text-center m-0" v-else>
        {{ $t("labels.dataLinkedTo") }}
        <strong>{{ aspspEmail || order.billingEmail }}</strong>
        {{ $t("labels.haveBeenUsedToFillTheForm") }}
        <br />
        {{ $t("labels.youWillBeBilledAt") }} :
        <strong>{{
          aspspFullAddress.billingAddress || fullAddress.billingAddress
        }}</strong
        >.
        <br />
        {{ $t("labels.youWillBeDeliveredAt") }} :
        <strong>{{
          aspspFullAddress.shippingAddress || fullAddress.shippingAddress
        }}</strong
        >.
      </p>
      <div class="text-right">
        <small>
          <a
            href="#"
            @click.prevent="editAspspPrefillAction"
            class="text-muted"
          >
            {{ $t("labels.editMyAddress") }}
          </a>
        </small>
      </div>
    </div>
    <span
      v-if="showPrefillAddressError"
      class="validation-error prefill-address-error"
      role="alert"
    >
      {{ $t("validation.mustSpecifyValidAddress") }}
    </span>
    <template v-for="(field, fieldName) in contactFormConfig">
      <div
        v-show="
          showGroup(field, fieldName) ||
          showMissingPrefilledField(field, fieldName)
        "
        :class="getGroupCustomCssClass(field, fieldName)"
      >
        <div
          v-if="field.tag === 'div'"
          :class="getCustomCssClass(v$, field, fieldName)"
          v-html="
            generateFieldLabel(field.label, field.variables, field.required)
          "
        ></div>
        <label
          v-else-if="field.type !== 'checkbox'"
          :for="fieldName"
          v-html="
            renderHTML(
              generateFieldLabel(field.label, field.variables, field.required),
            )
          "
        ></label>

        <template v-if="field.tag === 'input'">
          <input
            v-model.trim="order[fieldName]"
            :type="field.type"
            :name="fieldName"
            :id="fieldName"
            :placeholder="generateFieldLabel(field.placeholder, [])"
            :required="field.required"
            :checked="field.checked"
            :class="getCustomCssClass(v$, field, fieldName)"
            :autocomplete="getAutocompleteValue(fieldName)"
            @blur="onFieldBlur(fieldName)"
            @change="onFieldBlur(fieldName)"
            @focus="onContactFieldFocus(fieldName)"
            @input="onBrowserAddressAutofill($event)"
            @animationstart="onBrowserAddressAutofill($event)"
          />
          <span
            v-if="
              v$?.computedOrder[fieldName]?.$dirty &&
              v$?.computedOrder[fieldName]?.$error
            "
            class="validation-error"
            >{{ v$?.computedOrder[fieldName]?.$errors[0]?.$message }}</span
          >
        </template>
        <template v-else-if="field.tag === 'select'">
          <select
            v-model="order[fieldName]"
            :id="fieldName"
            :name="fieldName"
            :autocomplete="getAutocompleteValue(fieldName)"
            :disabled="isFieldDisabled(fieldName)"
            :class="getCustomCssClass(v$, field, fieldName)"
            @focus="onContactFieldFocus(fieldName)"
            @change="
              onFieldBlur(fieldName);
              onBrowserAddressAutofill($event);
            "
            @animationstart="onBrowserAddressAutofill($event)"
          >
            <option v-if="field.placeholder" value="" disabled="disabled">
              {{ generateFieldLabel(field.placeholder, []) }}
            </option>
            <option
              v-for="option in generateOptions(field.keyOption, field.options)"
              :name="fieldName"
              :value="option.value"
              :selected="order[fieldName] === option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </template>

        <label
          v-if="field.type === 'checkbox'"
          :for="fieldName"
          v-html="
            renderHTML(
              generateFieldLabel(field.label, field.variables, field.required),
            )
          "
        ></label>
      </div>
    </template>

    <Coregs />

    <div
      v-if="
        hasVisibleErrors &&
        (!isEligibleToOneClick || isOrderPageOneClickEligible)
      "
      class="alert alert-warning"
      role="alert"
    >
      <i
        class="fa-solid fa-triangle-exclamation"
        style="margin-right: 8px"
        aria-hidden="true"
      ></i>
      {{ $t("messages.invalidContactForm") }}
      <ul
        v-if="invalidFieldNames.length > 0"
        style="
          margin: 8px 0 0;
          padding-left: 20px;
          font-size: 13px;
          font-weight: 400;
        "
      >
        <li
          v-for="(fieldLabel, index) in invalidFieldNames"
          :key="`${fieldLabel}-${index}`"
        >
          {{ fieldLabel }}
        </li>
      </ul>
    </div>

    <div
      v-if="noItemInCart"
      class="alert alert-danger"
      role="alert"
      style="margin-top: 12px"
    >
      <i
        class="fa-solid fa-hand-point-left"
        style="margin-right: 8px"
        aria-hidden="true"
      ></i>
      {{ $t("messages.noItemInCart") }}
    </div>
  </form>
  <Modal
    :show-modal="showModal"
    :title="$t('modal.invalidCouponTitle')"
    @close="
      showModal = false;
      messages = [];
    "
    class="modal-invalid-coupon"
  >
    <div
      v-for="message in messages"
      :class="`alert alert-${message.type}`"
      role="alert"
    >
      {{ message.message }}
    </div>
  </Modal>
</template>

<script>
import { useCartStore } from "@/store/cart.js";
import { useDraftStore } from "@/store/draft.js";
import { useOrderHistoryStore } from "@/store/orderHistory.js";
import { useAspspStore } from "@/store/aspsp";
import Orion from "@/services/orion.js";
import { useTrackingStore } from "@/store/tracking";
import Pg from "@/services/pg";
import axios from "axios";
import { useVuelidate } from "@vuelidate/core";
import {
  required,
  email,
  sameAs,
  minLength,
  maxLength,
  helpers,
} from "@vuelidate/validators";
import CartService from "@/services/cart";
import Logger from "@/services/logger";
import optionsSelector from "@/i18n/optionsSelector";
import Utils from "@/services/utils";
import OneClickService from "@/services/payment/oneClick";
import { findCountryToBlock } from "@/services/validationForm/getCountryFromConfigSalesforce";
import { hasInvalidPrefillAddress } from "@/services/validationForm/hasInvalidPrefillAddress";
import {
  applyDomAddressToOrder,
  createDeferredCallback,
  getAutocompleteValue as autocompleteTokenForField,
  getMissingHiddenRequiredAddressFields,
  hasAddressPrefill,
  isBrowserAutofillEvent,
  readAddressFieldsFromDom,
  revealMissingAddressFieldGroups,
  shouldScheduleMissingAddressReveal,
  startBrowserAutofillWatch,
} from "@/services/browserAddressAutofill";
import postalCodes from "postal-codes-js";
import CouponService from "@/services/coupon";
import { getPaymentRetryOrderId } from "@/services/orderPageRetryParam";
import { normalizeContactFormTitleLabels } from "@/services/orderPageSectionTitles";
import {
  getVanguardOnlyContactFixture,
  isVanguardOnlyPreview,
} from "@/services/testFixtures";

import Modal from "@/components/checkout/Modal.vue";
import Coregs from "@/components/checkout/Coregs.vue";

export default {
  name: "CheckoutContactForm",
  components: { Modal, Coregs },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    const emailValidation = helpers.withMessage(
      () => this.getValidationMessage("validation._default"),
      required,
    );

    const sameAsBillingEmail = helpers.withMessage(
      () => this.getValidationMessage("validation.sameAsBillingEmail"),
      sameAs(this?.order.billingEmail),
    );

    const emailFormatValidation = helpers.withMessage(
      () => this.getValidationMessage("validation.emailInvalid"),
      email,
    );
    const firstNameRequired = helpers.withMessage(
      () => this.getValidationMessage("validation._default"),
      required,
    );
    const firstNameMinLength = helpers.withMessage(
      () => this.getValidationMessage("validation.firstNameMinLength"),
      minLength(2),
    );
    const firstNameMaxLength = helpers.withMessage(
      () => this.getValidationMessage("validation.firstNameMaxLength"),
      maxLength(40),
    );

    const lastNameRequired = helpers.withMessage(
      () => this.getValidationMessage("validation._default"),
      required,
    );

    const phoneNumberRequired = helpers.withMessage(
      () => this.getValidationMessage("validation._default"),
      required,
    );
    const lastNameMinLength = helpers.withMessage(
      () => this.getValidationMessage("validation.lastNameMinLength"),
      minLength(2),
    );
    const lastNameMaxLength = helpers.withMessage(
      () => this.getValidationMessage("validation.lastNameMaxLength"),
      maxLength(80),
    );

    const street3Required = helpers.withMessage(
      () => this.getValidationMessage("validation._default"),
      required,
    );

    const street3MinLength = helpers.withMessage(
      () => this.getValidationMessage("validation.street3MinLength"),
      minLength(6),
    );

    const street3MaxLength = helpers.withMessage(
      () => this.getValidationMessage("validation.street3MaxLength"),
      maxLength(96),
    );

    const zipCodeRequired = helpers.withMessage(
      () => this.getValidationMessage("validation._default"),
      required,
    );

    const cityRequired = helpers.withMessage(
      () => this.getValidationMessage("validation._default"),
      required,
    );

    const cityMaxLength = helpers.withMessage(
      () => this.getValidationMessage("validation.cityMaxLength"),
      maxLength(40),
    );

    const phoneNumberValidation = helpers.withMessage(
      () => this.getValidationMessage("validation.phoneNumberInvalid"),
      helpers.regex(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/i),
    );

    let billingEmailConfirmRules = {};

    const palyanceIframeAlwaysOn =
      window?.globalConfig?.palyanceIframeAlwaysOn || false;

    if (!palyanceIframeAlwaysOn) {
      billingEmailConfirmRules = {
        emailValidation,
        emailFormatValidation,
        sameAsBillingEmail,
      };
    }

    const localRules = {
      computedOrder: {
        billingEmail: { emailValidation, emailFormatValidation },
        billingEmailConfirm: billingEmailConfirmRules,
        billingFirstName: {
          firstNameRequired,
          firstNameMinLength,
          firstNameMaxLength,
        },
        billingLastName: {
          lastNameRequired,
          lastNameMinLength,
          lastNameMaxLength,
        },
        ...(this.contactFormConfig.billingPhoneNumber
          ? {
              billingPhoneNumber: {
                maxLength: maxLength(20),
                regex: phoneNumberValidation,
              },
            }
          : {}),
        billingStreet3: { street3Required, street3MinLength, street3MaxLength },
        billingStreet1: { street3MaxLength },
        billingStreet2: { street3MaxLength },
        billingStreet4: { street3MaxLength },
        billingZipCode: {
          zipCodeRequired,
          isValidPostalCode: this.isValidPostalCode("billingZipCode"),
        },
        billingCity: { cityRequired, cityMaxLength },
        billingCountry: { required, maxLength: maxLength(80) },
      },
    };

    if (this.contactFormConfig.billingPhoneNumber?.required) {
      localRules.computedOrder.billingPhoneNumber = {
        phoneNumberRequired,
        ...localRules.computedOrder.billingPhoneNumber,
      };
    }

    if (!this.order.sameAsBilling) {
      localRules.computedOrder.shippingFirstName = {
        firstNameRequired,
        firstNameMinLength,
        firstNameMaxLength,
      };
      localRules.computedOrder.shippingLastName = {
        lastNameRequired,
        lastNameMinLength,
        lastNameMaxLength,
      };
      if (this.contactFormConfig.shippingPhoneNumber) {
        localRules.computedOrder.shippingPhoneNumber = {
          maxLength: maxLength(20),
          regex: phoneNumberValidation,
        };
      }
      localRules.computedOrder.shippingStreet3 = {
        street3Required,
        street3MinLength,
        street3MaxLength,
      };
      localRules.computedOrder.shippingStreet1 = { street3MaxLength };
      localRules.computedOrder.shippingStreet2 = { street3MaxLength };
      localRules.computedOrder.shippingStreet4 = { street3MaxLength };
      localRules.computedOrder.shippingZipCode = {
        zipCodeRequired,
        isValidPostalCode: this.isValidPostalCode("shippingZipCode"),
      };
      localRules.computedOrder.shippingCity = { cityRequired, cityMaxLength };
      localRules.computedOrder.shippingCountry = {
        required,
        maxLength: maxLength(80),
      };

      if (this.contactFormConfig.shippingPhoneNumber?.required) {
        localRules.computedOrder.shippingPhoneNumber = {
          phoneNumberRequired,
          ...localRules.computedOrder.shippingPhoneNumber,
        };
      }
    }

    // FORGE-412 — Validation des cases CGV (acceptCgv) et CGS Palyance
    // (acceptCgs). Avant ce fix : aucune rule Vuelidate ne couvrait ces
    // champs, donc lorsque l'utilisateur cliquait sur "Je paye" sans les
    // cocher, Palyance retournait un message `contactFormCheckValidation`,
    // le parent scrollait vers les checkboxes via le fallback
    // `window.scrollTo({ top: 0 })`, mais aucun message d'erreur inline
    // n'apparaissait pres des cases (UX confuse).
    //
    // On n'applique la rule que si le champ est present dans
    // `contactFormConfig` (certains setups affilies n'ont pas de CGS
    // Palyance separes). Elle est neutralisee en mode 1-click puisque
    // le composant met `acceptCgv` / `acceptCgs` a `true` automatiquement
    // (cf. `handleOneClickFlow` plus bas) et que les groupes `cgv` sont
    // de toute facon caches via `showGroup`.
    const mustAcceptTerms = helpers.withMessage(
      () => this.getValidationMessage("validation.mustAcceptTerms"),
      (value) => value === true,
    );

    if (this.contactFormConfig.acceptCgv) {
      localRules.computedOrder.acceptCgv = { mustAcceptTerms };
    }
    if (this.contactFormConfig.acceptCgs) {
      localRules.computedOrder.acceptCgs = { mustAcceptTerms };
    }

    return localRules;
  },
  data() {
    return {
      cart: null,
      order: {}, // order data
      contactFormConfig: {}, // form config
      paysValidate: {},
      countryAutoSetBilling: false,
      countryAutoSetShipping: false,
      originalOrderPrefilled: {},
      showModal: false,
      messages: [],
      noItemInCart: false,
      emailLookupSeq: 0,
    };
  },
  computed: {
    lastCartUpdate() {
      return useCartStore().lastUpdate;
    },
    computedOrder() {
      /**
       * https://github.com/vuejs/vue/issues/2164#issuecomment-432872718
       * must be a new object to trigger vue reactivity (and get proper before / after state)
       */
      return Object.assign({}, this.order);
    },
    aspspPrefillData() {
      return useAspspStore().prefillData;
    },
    showBanner() {
      return (
        (useDraftStore().isEligibleToOneClick &&
          window.globalConfig.authorized1click) ||
        (useAspspStore().isEligible && !useAspspStore().aspspPrefillEditable)
      );
    },
    aspspEmail() {
      return useAspspStore().email;
    },
    aspspFullAddress() {
      return useAspspStore().aspspFullAdress;
    },
    loading() {
      return (
        useDraftStore().isPrefillLoading ||
        useDraftStore().isAspspEligibilityLoading
      );
    },
    isEligibleToOneClick() {
      return useDraftStore().isEligibleToOneClick;
    },
    isOrderPageOneClickEligible() {
      return (
        this.isEligibleToOneClick && window.globalConfig?.isOrderPage === true
      );
    },
    has1ClickOptOut() {
      return useDraftStore().has1ClickOptOut;
    },
    fullAddress() {
      return useDraftStore().fullAddress;
    },
    selectedPaymentMethod() {
      return useDraftStore().selectedPaymentMethod;
    },
    termsAndConditionsUrl() {
      return useDraftStore().termsAndConditionsUrl;
    },
    isDistinctAddress() {
      return useDraftStore().isDistinctAdress;
    },
    showPrefillAddressError() {
      return hasInvalidPrefillAddress({
        showBanner: this.showBanner,
        sameAsBilling: this.order.sameAsBilling,
        computedOrderValidator: this.v$?.computedOrder,
      });
    },
    hasVisibleErrors() {
      if (!this.v$?.computedOrder) return false;
      return (
        this.v$.computedOrder.$invalid ||
        !this.computedOrder.acceptCgs ||
        !this.computedOrder.acceptCgv
      );
    },
    invalidFieldNames() {
      if (!this.v$?.computedOrder) return [];
      const labels = [];
      const fieldLabelMap = {
        billingEmail: "contactForm.email",
        billingEmailConfirm: "contactForm.confirmationEmail",
        billingFirstName: "contactForm.firstName",
        billingLastName: "contactForm.name",
        billingPhoneNumber: "contactForm.phoneNumber",
        billingStreet3: "contactForm.address",
        billingZipCode: "contactForm.zipCode",
        billingCity: "contactForm.city",
        shippingFirstName: "contactForm.firstName",
        shippingLastName: "contactForm.name",
        shippingStreet3: "contactForm.address",
        shippingZipCode: "contactForm.zipCode",
        shippingCity: "contactForm.city",
      };
      for (const [field, labelKey] of Object.entries(fieldLabelMap)) {
        if (
          this.v$.computedOrder[field]?.$dirty &&
          this.v$.computedOrder[field]?.$error
        ) {
          labels.push(this.$t(labelKey));
        }
      }
      if (!this.computedOrder.acceptCgv) {
        labels.push(this.$t("contactForm.termsAndConditions"));
      }
      if (!this.computedOrder.acceptCgs) {
        labels.push(this.$t("contactForm.termsAndConditionsPalyance"));
      }
      return labels;
    },
  },
  watch: {
    lastCartUpdate: {
      handler(newValue, oldValue) {
        if (!window?.globalConfig?.domainId) {
          return;
        }
        this.cart = this.getCart();
      },
    },
    cart: {
      handler(newValue, oldValue) {
        if (!window?.globalConfig?.domainId) {
          return;
        }
        const previousSignature = this.order.cartSignature;
        const nextSignature = useCartStore().calcSignature();
        this.order.cartSignature = nextSignature;
        if (this.noItemInCart && newValue?.items?.length > 0) {
          this.noItemInCart = false;
          this.highlightOfferList(false);
        }
        // FORGE-412 — Quick-sell / changement d'offre : le panier Pinia
        // et l'iframe (postMessage amount) suivent, mais Palyance lit aussi
        // le draft Orion (choice + montant). Sans re-envoi, le tunnel garde
        // l'offre parente envoyee au premier draft (ex. auto-select).
        if (
          window.globalConfig?.isOrderPage &&
          previousSignature &&
          nextSignature &&
          previousSignature !== nextSignature &&
          useDraftStore().isDraftOrderSent &&
          useDraftStore().isFormValid &&
          newValue?.items?.length > 0 &&
          !useDraftStore().isSendingDraftOrder
        ) {
          this.resendDraftOrder();
        }
      },
      deep: true,
    },
    "order.acceptCgv": {
      handler(newValue, oldValue) {
        useDraftStore().approveCgv(newValue);
      },
    },
    termsAndConditionsUrl: {
      handler(newUrl) {
        if (newUrl && this.contactFormConfig.acceptCgv?.variables) {
          this.contactFormConfig.acceptCgv.variables.termsAndConditionsUrl =
            newUrl;
        }
      },
    },
    "order.acceptCgs": {
      handler(newValue, oldValue) {
        useDraftStore().approveCgs(newValue);
      },
    },
    "order.shippingCountry": {
      handler(newValue, oldValue) {
        useDraftStore().selectCountryCode(newValue);
        useCartStore().cart.shippingCountry = newValue;
        CartService.updateCart();
        this.processInfo("shipping");
      },
    },
    selectedPaymentMethod: {
      handler(newValue, oldValue) {
        this.order.paymentType = newValue; // trigger update of order and send it to orion
      },
    },
    computedOrder: {
      handler(newValue, oldValue) {
        useDraftStore().updateOrder(newValue);
        this.checkBillingFieldsCompletion();
        if (
          this.order.sameAsBilling &&
          Orion.needCopyBillingIntoShipping(this.order)
        ) {
          Orion.copyBillingIntoShipping(this.order);
        }
        if (
          this.order.acceptCgv &&
          this.order.acceptCgs &&
          this.v$.computedOrder.$invalid === false &&
          this.v$.computedOrder.$silentErrors.length === 0
        ) {
          useDraftStore().formValid(true);
        } else {
          useDraftStore().formValid(false);
        }

        if (
          useCartStore().cart?.items?.length > 0 &&
          (((oldValue.billingEmail !== newValue.billingEmail ||
            oldValue.billingEmailConfirm !== newValue.billingEmailConfirm) &&
            this.order.billingEmail === this.order.billingEmailConfirm) ||
            useDraftStore().isFormValid)
        ) {
          if (useDraftStore().isSendingDraftOrder) {
            // don't send previous event if we need to send another one
            window.clearTimeout(useDraftStore().isSendingDraftOrder);
          }

          const sendingDraftDelay =
            oldValue.paymentType != newValue.paymentType
              ? 0
              : window.contactFormConfig.sendingDraftDelay;

          // If either billingEmail or billingEmailConfirm has just  been edited and are equals
          // Or if the form is valid
          useDraftStore().sendingDraftOrder(
            window.setTimeout(async () => {
              console.log("sending draft order to orion");

              // store initial order id
              useDraftStore().updateInitialOrderData(
                useDraftStore().order.externalOrderId,
                useDraftStore().order.paymentType,
              );

              const preparedOrder = Orion.prepareOrder(
                useCartStore().cart,
                this.order,
                { orderType: "initial" },
              );
              useDraftStore().updateOrder(preparedOrder);
              console.log("preparedOrder contactForm", preparedOrder);

              const minifiedCart = CartService.minifyCart();

              if (!useDraftStore().isEligibleToOneClick) {
                await Orion.sendOrder(
                  minifiedCart,
                  preparedOrder,
                  window.globalConfig.domainId,
                );

                setTimeout(() => {
                  const cart = useCartStore().cart;
                  if (cart.coupons) {
                    const couponCodes = Object.keys(cart.coupons);
                    couponCodes.forEach((couponCode) => {
                      if (cart.coupons[couponCode].oneTimeUse === true) {
                        CouponService.checkCouponAlreadyUsed(
                          cart.coupons[couponCode].externalId,
                          preparedOrder.billingEmail.toLowerCase(),
                        ).then((isUsed) => {
                          if (isUsed) {
                            cart.coupons[couponCode].isValid = false;
                            cart.coupons[couponCode].reason = "ALREADY_USED";
                            this.messages.push({
                              type: "danger",
                              message: this.$t(
                                "messages.invalidCoupon.ALREADY_USED",
                                {
                                  invalidCouponCode:
                                    cart.coupons[couponCode].code,
                                  invalidCouponReason:
                                    cart.coupons[couponCode].reason,
                                },
                              ),
                            });
                            this.showModal = true;
                            CouponService.deleteCoupon(couponCode);
                          }
                        });
                      }
                    });
                  }
                });
              }

              useOrderHistoryStore().addOrderToHistory(
                useCartStore().cart,
                preparedOrder,
                0,
              );

              useDraftStore().sendingDraftOrder(false);
              useDraftStore().draftOrderSent(true);
            }, sendingDraftDelay),
          );
        }
      },
      deep: true,
    },
    aspspPrefillData: {
      handler(newValue, oldValue) {
        if (!newValue) {
          return;
        }
        this.prefill({
          billingEmail: newValue.EMAIL,
          billingGender: newValue.GENDER || null,
          billingFirstName: newValue.BILLINGFIRSTNAME
            ? newValue.BILLINGFIRSTNAME
            : newValue.FIRSTNAME,
          billingLastName: newValue.BILLINGLASTNAME
            ? newValue.BILLINGLASTNAME
            : newValue.LASTNAME,
          billingPhoneNumber: newValue.BILLINGPHONE__C
            ? newValue.BILLINGPHONE__C
            : newValue.BILLINGPHONE__C,
          billingStreet3: newValue.BILLINGSTREET3__C,
          billingStreet4: newValue.BILLINGSTREET4__C,
          billingStreet1: newValue.BILLINGSTREET1__C,
          billingStreet2: newValue.BILLINGSTREET2__C,
          billingZipCode: newValue.BILLINGZIPCODE__C,
          billingCity: newValue.BILLINGCITY__C,
          billingCountry: newValue.BILLINGCOUNTRY__C,
          shippingFirstName: newValue.SHIPPINGFIRSTNAME || newValue.FIRSTNAME,
          shippingLastName: newValue.SHIPPINGLASTNAME || newValue.LASTNAME,
          shippingPhoneNumber: newValue.SHIPPINGPHONE__C
            ? newValue.SHIPPINGPHONE__C
            : newValue.BILLINGPHONE__C,
          shippingStreet1:
            newValue.SHIPPINGSTREET1__C || newValue.BILLINGSTREET1__C,
          shippingStreet2:
            newValue.SHIPPINGSTREET2__C || newValue.BILLINGSTREET2__C,
          shippingStreet3:
            newValue.SHIPPINGSTREET3__C || newValue.BILLINGSTREET3__C,
          shippingStreet4:
            newValue.SHIPPINGSTREET4__C || newValue.BILLINGSTREET4__C,
          shippingZipCode:
            newValue.SHIPPINGZIPCODE__C || newValue.BILLINGZIPCODE__C,
          shippingCity: newValue.SHIPPINGCITY__C || newValue.BILLINGCITY__C,
          shippingCountry:
            newValue.SHIPPINGCOUNTRY__C || newValue.BILLINGCOUNTRY__C,
        });
        this.detectBillingEntity(null, true);
      },
      deep: true,
    },
    has1ClickOptOut: {
      handler(newValue) {
        this.order.has1ClickOptOut = newValue;
      },
    },
    "order.billingZipCode"(newZipCode) {
      this.processInfo("billing");
    },
    "order.billingCountry"(newCountry) {
      this.processInfo("billing");
    },
    "order.shippingZipCode"(newZipCode) {
      this.processInfo("shipping");
    },
    "order.sameAsBilling": {
      handler() {
        // Retrieves an array of all billing and shipping form fields
        const properties = this.v$.$silentErrors.map(
          (error) => error.$property,
        );
        // Retrieves an object with billing names as keys and shipping names as values
        const mapping = this.createFieldMapping(properties);
        this.syncBillingAndShippingErrors(this.v$, mapping);
      },
    },
  },
  mounted() {
    this.cart = this.getCart();
    this.contactFormConfig = normalizeContactFormTitleLabels(
      window.contactFormConfig.fields,
    );
    try {
      this.paysValidate = JSON.parse(window.globalConfig.mappingCountryZipcode); // get config saleforce zip_code ISO
    } catch (e) {
      console.error("Mapping country zipCode no found : ", e);
    }

    for (const fieldName of Object.keys(this.contactFormConfig)) {
      const config = this.contactFormConfig[fieldName];
      if (config.value !== undefined) {
        this.order[fieldName] = config.value;
      }
    }

    // Check for retry mode - prefill from previous order
    const searchParams = new URL(window.location).searchParams;
    const paymentRetryOrderId = getPaymentRetryOrderId(searchParams);
    if (paymentRetryOrderId) {
      this.prefillFromRetry(paymentRetryOrderId);
    }

    const regexIdentifier = /^[a-zA-Z0-9]*$/;
    const hasSfId =
      useTrackingStore().urlParams.sfId &&
      regexIdentifier.test(useTrackingStore().urlParams.sfId);
    const hasWalletId =
      useTrackingStore().urlParams.walletId &&
      regexIdentifier.test(useTrackingStore().urlParams.walletId);

    // Preview identity only when no real prefill (sfId/wallet) is present.
    if (
      isVanguardOnlyPreview() &&
      !paymentRetryOrderId &&
      !hasSfId &&
      !hasWalletId
    ) {
      this.prefill(getVanguardOnlyContactFixture());
      this.order.acceptCgv = true;
      this.order.acceptCgs = true;
      this.order.sameAsBilling = true;
      this.$nextTick(() => {
        this.v$.computedOrder.$touch();
        useDraftStore().formValid(true);
      });
    }

    if (hasSfId) {
      useDraftStore().prefillLoading(true);

      Pg.getPrefillData(
        useTrackingStore().urlParams.sfId,
        window.globalConfig.affiliateId,
      )
        .then((data) => {
          this.detectBillingEntity(data, true);
          const addressFields = [
            "billingStreet3",
            "billingStreet4",
            "billingStreet1",
            "billingStreet2",
            "billingZipCode",
            "billingCity",
            "billingCountry",
            "shippingStreet3",
            "shippingStreet4",
            "shippingStreet1",
            "shippingStreet2",
            "shippingZipCode",
            "shippingCity",
            "shippingCountry",
          ];
          for (const field of addressFields) {
            delete data[field];
          }
          this.prefill(data);
        })
        .catch((error) => {
          console.error(error);
          Logger.error("Error getting pgsf informations", error);
        });
    }

    window.addEventListener("message", this.iframeMessageCallback);
    this.startBrowserAutofillWatch();
    document.addEventListener("input", this.onBrowserAddressAutofill, true);
    document.addEventListener("change", this.onBrowserAddressAutofill, true);
    document.addEventListener(
      "animationstart",
      this.onBrowserAddressAutofill,
      true,
    );
  },
  beforeUnmount() {
    this.stopBrowserAutofillWatch();
    this._deferMissingAddressReveal?.cancel();
    document.removeEventListener("input", this.onBrowserAddressAutofill, true);
    document.removeEventListener("change", this.onBrowserAddressAutofill, true);
    document.removeEventListener(
      "animationstart",
      this.onBrowserAddressAutofill,
      true,
    );
  },
  methods: {
    getAutocompleteValue(fieldName) {
      return autocompleteTokenForField(fieldName);
    },
    startBrowserAutofillWatch() {
      this.stopBrowserAutofillWatch();
      this._stopBrowserAutofillWatch = startBrowserAutofillWatch(() => {
        this.applyBrowserAddressAutofill();
      });
    },
    stopBrowserAutofillWatch() {
      if (typeof this._stopBrowserAutofillWatch === "function") {
        this._stopBrowserAutofillWatch();
        this._stopBrowserAutofillWatch = null;
      }
    },
    onContactFieldFocus() {
      if (this.showBanner) {
        return;
      }
      this.applyBrowserAddressAutofill();
      this.startBrowserAutofillWatch();
    },
    onBrowserAddressAutofill(event) {
      if (this.showBanner || !isBrowserAutofillEvent(event)) {
        return;
      }
      this.applyBrowserAddressAutofill();
      this.$nextTick(() => {
        window.setTimeout(() => {
          this.applyBrowserAddressAutofill();
        }, 0);
      });
    },
    applyBrowserAddressAutofill() {
      if (this.showBanner) {
        return;
      }
      this.ensureDeferredMissingAddressReveal();
      const values = readAddressFieldsFromDom();
      applyDomAddressToOrder(this.order, values);
      if (
        shouldScheduleMissingAddressReveal(this.order, values, {
          sameAsBilling: this.order.sameAsBilling,
        })
      ) {
        this._deferMissingAddressReveal.schedule();
      }
    },
    ensureDeferredMissingAddressReveal() {
      if (this._deferMissingAddressReveal) {
        return;
      }
      this._deferMissingAddressReveal = createDeferredCallback(() => {
        this.revealMissingHiddenAddressFields();
      });
    },
    revealMissingHiddenAddressFields() {
      if (this.showBanner) {
        return;
      }
      if (!hasAddressPrefill(this.order)) {
        return;
      }
      const missing = getMissingHiddenRequiredAddressFields(this.order, {
        sameAsBilling: this.order.sameAsBilling,
      });
      if (missing.length === 0) {
        return;
      }
      revealMissingAddressFieldGroups(missing);
      this.$nextTick(() => {
        for (const fieldName of missing) {
          this.v$.computedOrder[fieldName]?.$touch();
        }
      });
    },
    getCart() {
      return useCartStore().cart;
    },
    iframeMessageCallback(event) {
      if (
        event &&
        event.data &&
        typeof event.data.contactFormCheckValidation !== "undefined"
      ) {
        console.debug(
          "event.data.contactFormCheckValidation",
          event.data.contactFormCheckValidation,
        );

        for (const fieldName of Object.keys(this.contactFormConfig)) {
          if (this.v$.computedOrder[fieldName]) {
            this.v$.computedOrder[fieldName].$touch();
          }
        }
        this.revealMissingHiddenAddressFields();

        if (useCartStore().cart?.items?.length === 0) {
          this.noItemInCart = true;
          this.highlightOfferList(true);
        } else {
          this.noItemInCart = false;
          this.highlightOfferList(false);
        }

        this.$nextTick(() => {
          if (this.noItemInCart) {
            const offerSection = document.querySelector(".order-page-process");
            if (offerSection) {
              offerSection.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
              return;
            }
          }
          const prefillAddressError = document.querySelector(
            ".contact-form .prefill-address-error",
          );
          if (prefillAddressError) {
            prefillAddressError.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            return;
          }
          const firstInvalid = document.querySelector(
            ".contact-form .is-invalid, .contact-form .validation-error",
          );
          if (firstInvalid) {
            firstInvalid.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        });
      }
    },
    highlightOfferList(highlight) {
      const offerSection = document.querySelector(".order-page-process");
      if (!offerSection) return;
      offerSection.classList.toggle("offer-section-highlight", highlight);
    },
    getGroupCustomCssClass(field, fieldName) {
      let cssClasses = `field-group-${field.group} ${fieldName}`;

      if (field.groupCssClass) {
        cssClasses += " " + field.groupCssClass;
      }

      return cssClasses;
    },
    getCustomCssClass(validator, field, fieldName) {
      let cssClasses = "field field-type-" + field.type + " field-" + fieldName;

      if (
        validator.computedOrder[fieldName]?.$dirty &&
        validator.computedOrder[fieldName]?.$error
      ) {
        // FORGE-645 — `.form-control.is-invalid` est dimensionne pour les
        // champs texte (padding + icone d'exclamation pleine largeur). Sur une
        // checkbox CGV en checkout shop, cela produit une barre rouge
        // horizontale (surtout visible apres prefill + `$touch()`).
        if (field.type === "checkbox") {
          cssClasses += " is-invalid";
        } else {
          cssClasses += " form-control is-invalid";
        }
      }

      if (field.cssClass) {
        cssClasses += " " + field.cssClass;
      }

      return cssClasses;
    },
    getValidationMessage(key) {
      this.$i18n.locale =
        window.globalConfig.lang || window.navigator.language.split("-")[0];
      return this.$i18n.t(key);
    },
    createFieldMapping(properties) {
      return properties.reduce((acc, prop) => {
        if (prop.startsWith("billing")) {
          acc[prop] = "shipping" + prop.slice(7);
        }
        return acc;
      }, {});
    },
    /**
     * Synchronizes the two forms to have the same errors when the checkbox is unchecked @validator
     * @param {Object} validator - The vuelidate validator object
     * @param {Object} fieldMap - The mapping between billing and shipping fields
     * @returns {void}
     * */
    syncBillingAndShippingErrors(validator, fieldMap) {
      const errors = validator.$errors;
      for (const error of errors) {
        const field = Object.keys(fieldMap).find((billingField) =>
          error.$property.includes(billingField),
        );
        if (field) {
          validator.computedOrder[fieldMap[field]]?.$touch();
        }
      }
    },
    isValidPostalCode(field) {
      const validateZipCode = (country, zipCode) => {
        return (
          country === "ICA" &&
          (zipCode.startsWith("35") || zipCode.startsWith("38")) &&
          /^\d{5}$/.test(zipCode)
        );
      };

      if (
        validateZipCode(this.order.billingCountry, this.order.billingZipCode) ||
        validateZipCode(this.order.shippingCountry, this.order.shippingZipCode)
      ) {
        return true;
      }

      return helpers.withMessage(
        () => this.getValidationMessage("validation.zipCodeInvalid"),
        helpers.withParams({ type: "isValidPostalCode" }, (value) => {
          return !value
            ? true
            : postalCodes.validate(
                field === "billingZipCode"
                  ? this.order.billingCountry
                  : this.order.shippingCountry,
                value,
              ) === true;
        }),
      );
    },
    isFieldDisabled(fieldName) {
      const fieldToPropertyMapping = {
        billingCountry: "countryAutoSetBilling",
        shippingCountry: "countryAutoSetShipping",
      };

      if (
        !fieldToPropertyMapping[fieldName] ||
        !this[fieldToPropertyMapping[fieldName]]
      ) {
        return false;
      }

      return this[fieldToPropertyMapping[fieldName]];
    },
    processInfo(type) {
      const zipCode = this.order[`${type}ZipCode`];
      const country = this.order[`${type}Country`];

      // Expected length for zip codes in the countries concerned
      const EXPECTED_ZIP_CODE_LENGTH = 5;

      if (zipCode?.length === EXPECTED_ZIP_CODE_LENGTH) {
        let countryBlocked = findCountryToBlock(
          zipCode,
          country,
          this.paysValidate,
        );
        if (countryBlocked) {
          if (countryBlocked === " blocked") {
            this.order[`${type}Country`] = null;
            this[`countryAutoSet${Utils.capitalize(type)}`] = true;
          } else {
            this.order[`${type}Country`] = countryBlocked;
            this[`countryAutoSet${Utils.capitalize(type)}`] = true;
          }
        }
      }
      if (!zipCode || zipCode.length < EXPECTED_ZIP_CODE_LENGTH) {
        this[`countryAutoSet${Utils.capitalize(type)}`] = false;
      }
    },
    editAspspPrefillAction() {
      this.order.acceptCgs = false;
      this.order.acceptCgv = false;
      useDraftStore().eligibleToOneClick(false);
      useAspspStore().updateAspspPrefillEditable(true);
      this.order.sameAsBilling = false;
    },
    showGroup(field, fieldName) {
      const group = field.group;
      if (this.isEligibleToOneClick && group === "cgv") {
        // FORGE-412 — Sur order page en 1-click, les cases CGV/CGS restent
        // visibles pour debloquer le bouton de paiement (pas de lien CGV
        // redondant sous le bouton).
        if (window.globalConfig?.isOrderPage) {
          return true;
        }
        return false;
      }
      if (this.showBanner && (group === "billing" || group === "shipping")) {
        return false;
      }
      // FORGE-412 — Le toggle `sameAsBilling` est un controle UI qui
      // appartient au bloc "saisie d'adresse". En mode prefill (walletId
      // resolu ou 1-click eligible, showBanner=true), l'adresse est
      // presentee dans l'alert bleu et l'utilisateur doit cliquer
      // "Editer mon adresse" pour reveler les champs : a ce moment-la
      // seulement, `aspspPrefillEditable` passe a true, `showBanner`
      // bascule a false, et le toggle redevient pertinent. Selon la
      // config Salesforce, `sameAsBilling` peut avoir un `group` autre
      // que "billing" (cas observe sur certaines order-pages), donc on
      // garde une garde explicite par nom de field plutot que de se
      // reposer uniquement sur la regle group-based ci-dessus.
      if (this.showBanner && fieldName === "sameAsBilling") {
        return false;
      }
      if (group === "shipping" && this.order.sameAsBilling) {
        return false;
      }

      return true;
    },
    showMissingPrefilledField(field, fieldName) {
      // FORGE-412 — Les checkboxes/toggles (sameAsBilling, acceptCgv,
      // acceptCgs) ne sont pas des "donnees a pre-remplir" mais des
      // controles UI. Les inclure ici aurait pour effet de forcer le
      // toggle sameAsBilling a s'afficher en mode prefill (walletId
      // resolu / 1-click), alors que `showGroup()` le cache deliberement.
      // Resultat sans cette garde : le bloc bleu "Vos donnees ont ete
      // utilisees pour remplir le formulaire" et le toggle s'affichaient
      // simultanement, incoherent (le toggle ne sert a rien tant que
      // l'utilisateur n'a pas cliqué "Editer mon adresse").
      if (field.type === "checkbox") {
        return false;
      }
      if (
        field.group === "billing" ||
        (field.group === "shipping" && !this.order.sameAsBilling)
      ) {
        const showMissingPrefilledField =
          field.required &&
          (!this.originalOrderPrefilled?.[fieldName] ||
            this.originalOrderPrefilled?.[fieldName] === "");

        if (showMissingPrefilledField) {
          this.v$.computedOrder?.[fieldName]?.$touch();
          return true;
        }
      }

      return false;
    },
    prefill(data) {
      let isCountryBlocked = false;
      if (
        window.globalConfig.countriesExcluded &&
        window.globalConfig.countriesExcluded.includes(
          data.billingCountry || data.shippingCountry,
        )
      ) {
        isCountryBlocked = true;
        this.editAspspPrefillAction();
        useAspspStore().eligible(false);
      }

      let sameAsBilling = true;
      if (data.shippingStreet3) {
        const shippingFields = [
          "shippingStreet3",
          "shippingZipCode",
          "shippingCity",
          "shippingCountry",
          "shippingStreet1",
          "shippingStreet2",
          "shippingStreet4",
        ];
        sameAsBilling = shippingFields.every(
          (field) => data[field] === data[field.replace("shipping", "billing")],
        );
      }

      const acceptConditionGeneral = isCountryBlocked
        ? null
        : OneClickService.prefillOneClick(data);
      this.order = {
        ...this.order,
        ...acceptConditionGeneral,
      };
      const assignIfPresent = (field, value) => {
        if (value !== undefined) this.order[field] = value;
      };

      assignIfPresent("billingEmail", data.billingEmail);
      assignIfPresent("billingEmailConfirm", data.billingEmail);
      assignIfPresent("billingGender", data.billingGender);
      assignIfPresent("billingFirstName", data.billingFirstName);
      assignIfPresent("billingLastName", data.billingLastName);
      assignIfPresent("billingPhoneNumber", data.billingPhoneNumber);
      assignIfPresent(
        "billingStreet3",
        isCountryBlocked ? null : data.billingStreet3,
      );
      assignIfPresent(
        "billingStreet4",
        isCountryBlocked ? null : data.billingStreet4,
      );
      assignIfPresent(
        "billingStreet1",
        isCountryBlocked ? null : data.billingStreet1,
      );
      assignIfPresent(
        "billingStreet2",
        isCountryBlocked ? null : data.billingStreet2,
      );
      assignIfPresent(
        "billingZipCode",
        isCountryBlocked ? null : data.billingZipCode,
      );
      assignIfPresent(
        "billingCity",
        isCountryBlocked ? null : data.billingCity,
      );
      assignIfPresent(
        "billingCountry",
        isCountryBlocked ? null : data.billingCountry,
      );
      assignIfPresent("shippingFirstName", data.shippingFirstName);
      assignIfPresent("shippingLastName", data.shippingLastName);
      assignIfPresent(
        "shippingCity",
        isCountryBlocked ? null : data.shippingCity,
      );
      assignIfPresent(
        "shippingCountry",
        isCountryBlocked ? null : data.shippingCountry,
      );
      assignIfPresent("shippingPhoneNumber", data.shippingPhoneNumber);
      assignIfPresent(
        "shippingStreet1",
        isCountryBlocked ? null : data.shippingStreet1,
      );
      assignIfPresent(
        "shippingStreet2",
        isCountryBlocked ? null : data.shippingStreet2,
      );
      assignIfPresent(
        "shippingStreet3",
        isCountryBlocked ? null : data.shippingStreet3,
      );
      assignIfPresent(
        "shippingStreet4",
        isCountryBlocked ? null : data.shippingStreet4,
      );
      assignIfPresent(
        "shippingZipCode",
        isCountryBlocked ? null : data.shippingZipCode,
      );
      this.order.sameAsBilling = sameAsBilling;

      this.originalOrderPrefilled = {
        ...this.order,
      };
      this.$nextTick(() => {
        this.v$.computedOrder.$touch();
        // Les CGV restent invalides tant que non cochees, mais on evite
        // d'afficher le message inline sous chaque checkbox des le prefill.
        ["acceptCgv", "acceptCgs"].forEach((fieldName) => {
          this.v$.computedOrder?.[fieldName]?.$reset();
        });
      });
      useDraftStore().prefillLoading(false);
    },
    prefillFromRetry(retryOrderId) {
      const retryItem = useOrderHistoryStore().currentPageOrderHistory.find(
        (item) => item.order.externalOrderId === retryOrderId,
      );

      if (retryItem && retryItem.order) {
        const previousOrder = retryItem.order;

        // Prefill form with previous order data
        this.order.billingEmail = previousOrder.billingEmail;
        this.order.billingEmailConfirm = previousOrder.billingEmail;
        this.order.billingGender = previousOrder.billingGender;
        this.order.billingFirstName = previousOrder.billingFirstName;
        this.order.billingLastName = previousOrder.billingLastName;
        this.order.billingPhoneNumber = previousOrder.billingPhoneNumber;
        this.order.billingStreet1 = previousOrder.billingStreet1;
        this.order.billingStreet2 = previousOrder.billingStreet2;
        this.order.billingStreet3 = previousOrder.billingStreet3;
        this.order.billingStreet4 = previousOrder.billingStreet4;
        this.order.billingZipCode = previousOrder.billingZipCode;
        this.order.billingCity = previousOrder.billingCity;
        this.order.billingCountry = previousOrder.billingCountry;
        this.order.shippingFirstName = previousOrder.shippingFirstName;
        this.order.shippingLastName = previousOrder.shippingLastName;
        this.order.shippingPhoneNumber = previousOrder.shippingPhoneNumber;
        this.order.shippingStreet1 = previousOrder.shippingStreet1;
        this.order.shippingStreet2 = previousOrder.shippingStreet2;
        this.order.shippingStreet3 = previousOrder.shippingStreet3;
        this.order.shippingStreet4 = previousOrder.shippingStreet4;
        this.order.shippingZipCode = previousOrder.shippingZipCode;
        this.order.shippingCity = previousOrder.shippingCity;
        this.order.shippingCountry = previousOrder.shippingCountry;
        this.order.sameAsBilling = previousOrder.sameAsBilling;

        // Auto-accept CGV/CGS for retry (user already accepted before)
        this.order.acceptCgv = true;
        this.order.acceptCgs = true;

        this.originalOrderPrefilled = {
          ...this.order,
        };

        console.log("Prefilled contact form from retry order:", retryOrderId);
      }
    },
    translateError(error) {
      let message = error.$message;

      if (["minLength", "maxLength"].includes(error.$validator)) {
        for (const paramKey in error.$params) {
          message = message.replace(error.$params[paramKey], `{${paramKey}}`);
        }
      }
      return this.$t(`errors.${message}`, error.$params);
    },
    generateFieldLabel(label, variables, required = false) {
      if (!label) {
        return;
      }
      const labelSplit = label.split(" ");
      const labelRequired = required
        ? "%3Cspan%20class%3D%22required%22%3E*%3C%2Fspan%3E"
        : ""; // add * on the label field
      return (
        labelSplit
          .map((word) => {
            if (word.startsWith("$")) {
              return this.$t(`contactForm.${word.substring(1)}`);
            } else if (word.startsWith("#")) {
              return variables[word.substring(1)];
            } else {
              return word;
            }
          })
          .join(" ") +
        " " +
        labelRequired
      );
    },
    onFieldBlur(fieldName) {
      if (this.v$.computedOrder[fieldName]) {
        this.v$.computedOrder[fieldName].$touch();
      }
      if (fieldName === "billingEmail") {
        this.onEmailBlur();
      }
    },
    detectBillingEntity(contactData, isKnownContact = false) {
      const jc = window.globalConfig.journeyConfig;
      const config = jc
        ? jc.billingEntityConfig
        : window.globalConfig.billingEntityConfig;
      if (!config || !config.entities) {
        return;
      }

      if (contactData && contactData.billingEntity) {
        const match = Object.values(config.entities).find(
          (e) => e.sfId === contactData.billingEntity,
        );
        useDraftStore().updateBillingEntity(
          contactData.billingEntity,
          match ? match.name : null,
        );
        useDraftStore().updateTermsUrlForEntity(contactData.billingEntity);
        console.log(
          "[vanguard-v2] BillingEntity from prefill:",
          contactData.billingEntity,
        );
        return;
      }

      const knownKey =
        Object.keys(config.entities).find(
          (k) => config.entities[k].name === "OLI_CH",
        ) || "1";
      const fallbackKey = isKnownContact ? knownKey : config.defaultExternalId;
      const entity = config.entities[fallbackKey];
      if (entity) {
        useDraftStore().updateBillingEntity(entity.sfId, entity.name);
        if (isKnownContact) {
          useDraftStore().updateTermsUrlForEntity(entity.sfId);
        }
        console.log(
          `[vanguard-v2] BillingEntity ${isKnownContact ? "known client" : "default"}:`,
          entity.sfId,
          entity.name,
        );
      }
    },
    async onEmailBlur() {
      if (useTrackingStore().urlParams.sfId) return;
      const lookupSeq = ++this.emailLookupSeq;
      const email = this.order.billingEmail;
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return;
      }
      const baseUrl = process.env.VUE_APP_SALESFORCE_DATA_PUBLIC_URL;
      if (!baseUrl) {
        return;
      }
      try {
        const normalized = email.toLowerCase().trim();
        const hashBuffer = await crypto.subtle.digest(
          "SHA-256",
          new TextEncoder().encode(normalized),
        );
        const emailHash = Array.from(new Uint8Array(hashBuffer))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");

        const url = `${baseUrl}/lookup?emailHash=${emailHash}&affiliateId=${window.globalConfig.affiliateId}`;
        const response = await axios.get(url).catch((err) => {
          if (err.response && err.response.status === 404)
            return { data: null };
          throw err;
        });
        // A later blur owns the entity: an older lookup must not overwrite it.
        if (lookupSeq !== this.emailLookupSeq) return;

        if (response.data && response.data.billingEntityId) {
          useDraftStore().updateBillingEntity(
            response.data.billingEntityId,
            response.data.billingEntityName || null,
          );
          useDraftStore().updateTermsUrlForEntity(
            response.data.billingEntityId,
          );
          console.log(
            "[vanguard-v2] BillingEntity from DynamoDB:",
            response.data.billingEntityId,
          );
        } else {
          useDraftStore().resetBillingEntity();
          console.log(
            "[vanguard-v2] BillingEntity reset to default (new contact)",
          );
        }
        this.resendDraftOrder();
      } catch (error) {
        console.warn("[vanguard-v2] Email lookup failed:", error);
      }
    },
    async resendDraftOrder() {
      const cart = useCartStore().cart;
      if (!cart?.items?.length) return;
      const preparedOrder = Orion.prepareOrder(cart, this.order, {
        orderType: "initial",
      });
      useDraftStore().updateOrder(preparedOrder);
      const minifiedCart = CartService.minifyCart();
      if (!useDraftStore().isEligibleToOneClick) {
        await Orion.sendOrder(
          minifiedCart,
          preparedOrder,
          window.globalConfig.domainId,
        );
      }
      console.log("[vanguard-v2] Draft re-sent with updated billingEntity");
    },
    generateOptions(key, oldOptions) {
      let options = [];
      const language = window.globalConfig.lang;
      for (const option in optionsSelector[key]) {
        options.push({
          label: optionsSelector[key][option][language],
          value: option,
        });
      }

      if (key === "countries") {
        if (window.globalConfig.countriesExcluded) {
          const countriesExcluded =
            window.globalConfig.countriesExcluded.split(";");
          options = options.filter(
            (option) => !countriesExcluded.includes(option.value),
          );
        }
        options = Utils.sortAlphabetically(options, "label");
      }

      // @Todo Delete after one or two week after this deployment
      if (!options.length) {
        return oldOptions;
      }

      return options;
    },
    async checkBillingFieldsCompletion() {
      const requiredBillingFields = [
        "billingEmail",
        "billingFirstName",
        "billingLastName",
        "billingStreet3",
        "billingZipCode",
        "billingCity",
        "billingCountry",
      ];

      const isComplete = await requiredBillingFields.every((field) => {
        const hasValue = this.order[field] && this.order[field].trim() !== "";
        const isValid = this.v$.computedOrder[field]
          ? !this.v$.computedOrder[field].$error
          : true;

        return hasValue && isValid;
      });

      useDraftStore().updateBillingFormCompletion(isComplete);
      return isComplete;
    },
  },
};
</script>
